/**
 * Unsaved-changes guard for the multi-field editors — PRO-2 (profile) and PRO-4 (wizard),
 * per docs/06 §4.1.
 *
 * Two escape routes have to be covered, and they are different mechanisms:
 *   1. Leaving the SPA entirely (tab close, reload, external link) → `beforeunload`, which only
 *      permits the browser's own generic prompt.
 *   2. Navigating within the SPA (sidebar click, back button) → React Router. We use the
 *      `useBlocker` hook, which requires a data router (`createBrowserRouter`) — which is why
 *      `App.tsx` uses one.
 */
import * as React from 'react';
import { useBlocker, type BlockerFunction } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from '@/components/ui';

export function UnsavedGuard({ when }: { when: boolean }) {
  const { t } = useTranslation();

  // In-SPA navigation.
  const blocker = useBlocker(
    React.useCallback<BlockerFunction>(
      ({ currentLocation, nextLocation }) =>
        when && currentLocation.pathname !== nextLocation.pathname,
      [when],
    ),
  );

  // Leaving the page entirely. The browser ignores our copy and shows its own dialog; setting
  // `returnValue` is the only thing that triggers it at all.
  React.useEffect(() => {
    if (!when) return;

    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [when]);

  return (
    <ConfirmDialog
      open={blocker.state === 'blocked'}
      onClose={() => blocker.reset?.()}
      onConfirm={() => blocker.proceed?.()}
      title={t('common.unsavedTitle')}
      description={t('common.unsavedBody')}
      confirmLabel={t('common.unsavedLeave')}
      cancelLabel={t('common.unsavedStay')}
      destructive
    />
  );
}
